import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'
import { API_URL, AppConstant } from '../../../../AppConstant'
import { APIStatus } from '../../../types'

// Types
interface Doctor {
  id: string
  userId: string
  firstName: string
  lastName: string
  email?: string
  // Add other doctor fields as needed
}

interface DoctorState {
  doctor: Doctor | null
  status: APIStatus
  error: string | null
}

// Initial State
const initialState: DoctorState = {
  doctor: null,
  status: APIStatus.idle,
  error: null,
}

// Async Thunks
export const fetchDoctorAsync = createAsyncThunk('doctor/fetchDoctor', async (doctorId: string, thunkAPI) => {
  try {
    const token = localStorage.getItem(AppConstant.ACCESS_TOKEN)

    const response = await axios.get(`${API_URL}/doctor/${doctorId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Platform-Id': process.env.REACT_APP_PLATFORM_ID,
      },
    })

    if (response.data && response.data.code === 'success') {
      return thunkAPI.fulfillWithValue(response.data.data)
    } else {
      return thunkAPI.rejectWithValue(response.data.message || 'Failed to fetch doctor')
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred'
    return thunkAPI.rejectWithValue(errorMessage)
  }
})

// Slice
const doctorSlice = createSlice({
  name: 'doctor',
  initialState,
  reducers: {
    setDoctor: (state, action: PayloadAction<Doctor>) => {
      state.doctor = action.payload
    },
    clearDoctor: (state) => {
      state.doctor = null
      state.error = null
    },
    resetStatus: (state) => {
      state.status = APIStatus.idle
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDoctorAsync.pending, (state) => {
        state.status = APIStatus.pending
        state.error = null
      })
      .addCase(fetchDoctorAsync.fulfilled, (state, action) => {
        state.status = APIStatus.fulfilled
        state.doctor = action.payload
        state.error = null
      })
      .addCase(fetchDoctorAsync.rejected, (state, action) => {
        state.status = APIStatus.rejected
        state.error = action.payload as string
      })
  },
})

// Export actions
export const { setDoctor, clearDoctor, resetStatus } = doctorSlice.actions

// Export reducer
export default doctorSlice.reducer
